import { NavLink, Link } from "react-router-dom";
import { useState } from "react";
import { List, X } from "@phosphor-icons/react";
import { useSettings } from "@/contexts/SettingsContext";
import { resolveImage } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/products", label: "Products" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function LogoMark({ logoUrl }) {
  const [errored, setErrored] = useState(false);
  if (logoUrl && !errored) {
    return (
      <img
        src={resolveImage(logoUrl)}
        alt="OneTec"
        className="h-9 md:h-10 w-auto"
        onError={() => setErrored(true)}
      />
    );
  }
  // Fallback wordmark if the image fails or is missing
  return (
    <span className="font-display text-2xl md:text-3xl font-bold tracking-tighter uppercase leading-none">
      One<span className="bg-ot-peach px-1 border-2 border-ot-fg">Tec</span>
    </span>
  );
}

export default function NavBar() {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const logoUrl = settings?.nav_logo_url;

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-ot-bg/80 border-b-2 border-ot-fg"
      data-testid="site-navbar"
      role="banner"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-5 md:px-10 py-4">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo" aria-label="OneTec — home">
          <LogoMark logoUrl={logoUrl} />
        </Link>

        <nav className="hidden md:flex items-center gap-2" aria-label="Primary navigation">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-display uppercase tracking-wide text-sm px-3 py-2 transition-all border-2 ${
                  isActive
                    ? "bg-ot-indigo text-white border-ot-indigo"
                    : "border-transparent hover:bg-ot-peach hover:border-ot-fg"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/contact" className="nb-btn ml-3" data-testid="nav-cta-book">
            Book a call
          </Link>
        </nav>

        <button
          className="md:hidden border-2 border-ot-fg p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t-2 border-ot-fg bg-ot-bg" data-testid="mobile-nav-menu">
          <div className="flex flex-col">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-5 py-4 border-b border-ot-fg/10 font-display uppercase ${
                    isActive ? "bg-ot-peach" : ""
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
